import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IZoneGroup, IZoneGroupInput, zoneGroupsAtom, zoneGroupsLoadingAtom } from "./types";

type UseZoneGroups = [IZoneGroup[], {
    list: (query?: Record<string, any>) => Promise<IZoneGroup[] | null>;
    getById: (id: number) => Promise<IZoneGroup | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IZoneGroup[] | null>;
    create: (input: IZoneGroupInput) => Promise<IZoneGroup | null>;
    update: (id: number, input: IZoneGroupInput) => Promise<IZoneGroup | null>;
    remove: (id: number) => Promise<IZoneGroup | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useZoneGroups = (): UseZoneGroups => {
    const [items, setItems] = useAtom(zoneGroupsAtom);
    const setLoading = useSetAtom(zoneGroupsLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getZoneGroups(query: $query){ id name description width height length x y z warehouse_id created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getZoneGroups);
            return data.getZoneGroups as IZoneGroup[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch zone groups.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getZoneGroupById(id:$id){ id name description width height length x y z warehouse_id created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getZoneGroupById as IZoneGroup;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedZoneGroups(first:$first,offset:$offset,query:$query){ id name description width height length x y z warehouse_id created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedZoneGroups as IZoneGroup[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IZoneGroupInput) => {
        try { const q = `mutation($input:ZoneGroupInput!){ createZoneGroup(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createZoneGroup as IZoneGroup;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IZoneGroupInput) => {
        try { const q = `mutation($id:ID!,$input:ZoneGroupInput!){ updateZoneGroup(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateZoneGroup as IZoneGroup;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteZoneGroup(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteZoneGroup as IZoneGroup;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


